/* ============================================
   StrategyPanel — Configuración de estrategia
   Brand voice, audiencia, pilares, hashtags,
   estilo visual, guidelines
   ============================================ */
import { useState, useEffect } from 'react'
import {
  IconPlus, IconTrash, IconCheck, IconTarget,
  IconAlertCircle, IconMicrophone, IconUsers,
  IconPalette, IconBarChart, IconFileText, IconHash,
} from '@/components/icons/Icons'
import HashtagManager from './HashtagManager'
import type { SocialStrategy, ContentPillar, HashtagGroup } from '@/lib/types'

interface StrategyPanelProps {
  strategy: SocialStrategy | null
  onSave: (strategy: Partial<SocialStrategy>) => void
}

export default function StrategyPanel({ strategy, onSave }: StrategyPanelProps) {
  const [brandVoice, setBrandVoice] = useState('')
  const [targetAudience, setTargetAudience] = useState('')
  const [pillars, setPillars] = useState<ContentPillar[]>([])
  const [hashtagGroups, setHashtagGroups] = useState<HashtagGroup[]>([])
  const [visualStyle, setVisualStyle] = useState('')
  const [guidelines, setGuidelines] = useState('')
  const [isSaving, setIsSaving] = useState(false)

  // Sync from prop
  useEffect(() => {
    if (strategy) {
      setBrandVoice(strategy.brand_voice || '')
      setTargetAudience(strategy.target_audience || '')
      setPillars(strategy.content_pillars || [])
      setHashtagGroups(strategy.hashtag_groups || [])
      setVisualStyle(strategy.visual_style || '')
      setGuidelines(strategy.guidelines || '')
    }
  }, [strategy])

  // ─── Pillar helpers ───
  const pillarTotal = pillars.reduce((sum, p) => sum + (p.percentage || 0), 0)
  const pillarError = pillars.length > 0 && pillarTotal !== 100

  function handleAddPillar(): void {
    setPillars([...pillars, { name: '', description: '', percentage: 0 }])
  }

  function handleRemovePillar(index: number): void {
    setPillars(pillars.filter((_, i) => i !== index))
  }

  function handlePillarChange(
    index: number,
    field: keyof ContentPillar,
    value: string | number
  ): void {
    setPillars(
      pillars.map((p, i) =>
        i === index ? { ...p, [field]: value } : p
      )
    )
  }


  // ─── Save ───
  async function handleSave(): Promise<void> {
    setIsSaving(true)
    try {
      await onSave({
        brand_voice: brandVoice || null,
        target_audience: targetAudience || null,
        content_pillars: pillars,
        hashtag_groups: hashtagGroups,
        visual_style: visualStyle || null,
        guidelines: guidelines || null,
      })
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="strategy-panel">
      <div className="strategy-panel__header">
        <div>
          <h2>
            <IconTarget size={20} /> Estrategia de Marca
          </h2>
          <p className="page-subtitle">
            Define la voz, audiencia y pilares de contenido para tu marca
          </p>
        </div>
      </div>

      <div className="strategy-panel__body">
        <div className="strategy-grid">
          {/* Col 1: Identity & Voice */}
          <div className="strategy-col">
            <div className="glass-card strategy-card">
              <div className="strategy-card__header">
                <h3><IconMicrophone size={16} /> Identidad de Marca</h3>
              </div>
              <div className="strategy-card__body">
                {/* Brand Voice */}
                <div className="form-group">
                  <label className="form-label" style={{ display: 'block', marginBottom: 6 }}>Voz de Marca</label>
                  <textarea
                    className="form-textarea strategy-textarea"
                    placeholder="Describe el tono de comunicación de tu marca. Ej: profesional pero cercano, con un toque de humor..."
                    value={brandVoice}
                    onChange={e => setBrandVoice(e.target.value)}
                    rows={4}
                  />
                </div>

                {/* Target Audience */}
                <div className="form-group" style={{ marginTop: 16 }}>
                  <label className="form-label" style={{ display: 'block', marginBottom: 6 }}><IconUsers size={14} /> Audiencia Objetivo</label>
                  <textarea
                    className="form-textarea strategy-textarea"
                    placeholder="Describe tu audiencia ideal. Ej: Emprendedores y pymes de 25-45 años en Latinoamérica..."
                    value={targetAudience}
                    onChange={e => setTargetAudience(e.target.value)}
                    rows={4}
                  />
                </div>
              </div>
            </div>

            {/* Visual Style & Guidelines */}
            <div className="glass-card strategy-card" style={{ marginTop: 24 }}>
              <div className="strategy-card__header">
                <h3><IconPalette size={16} /> Estilo Visual & Directrices</h3>
              </div>
              <div className="strategy-card__body">
                {/* Visual Style */}
                <div className="form-group">
                  <label className="form-label" style={{ display: 'block', marginBottom: 6 }}>Estilo Visual</label>
                  <textarea
                    className="form-textarea strategy-textarea"
                    placeholder="Describe la estética visual preferida. Ej: minimalista, colores vibrantes, fotos lifestyle..."
                    value={visualStyle}
                    onChange={e => setVisualStyle(e.target.value)}
                    rows={3}
                  />
                </div>

                {/* Guidelines */}
                <div className="form-group" style={{ marginTop: 16 }}>
                  <label className="form-label" style={{ display: 'block', marginBottom: 6 }}><IconFileText size={14} /> Instrucciones Generales para la IA</label>
                  <textarea
                    className="form-textarea strategy-textarea"
                    placeholder="Reglas y lineamientos que la IA debe seguir al generar contenido..."
                    value={guidelines}
                    onChange={e => setGuidelines(e.target.value)}
                    rows={4}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Col 2: Content Strategy & Competitors */}
          <div className="strategy-col">
            {/* Content Pillars */}
            <div className="glass-card strategy-card">
              <div className="strategy-card__header">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                  <h3><IconBarChart size={16} /> Pilares de Contenido</h3>
                  <div className="strategy-section-actions">
                    {pillars.length > 0 && (
                      <span
                        className={`strategy-pillar-total ${
                          pillarError ? 'strategy-pillar-total--error' : 'strategy-pillar-total--ok'
                        }`}
                      >
                        {pillarTotal}%
                        {pillarError && <IconAlertCircle size={12} />}
                      </span>
                    )}
                    <button
                      type="button"
                      className="btn btn-sm btn-secondary"
                      onClick={handleAddPillar}
                    >
                      <IconPlus size={14} /> Pilar
                    </button>
                  </div>
                </div>
              </div>
              <div className="strategy-card__body">
                {pillars.length === 0 && (
                  <p className="strategy-empty-hint">
                    Agrega pilares para organizar tu estrategia de contenido.
                  </p>
                )}

                <div className="strategy-pillars">
                  {pillars.map((pillar, i) => (
                    <div key={i} className="strategy-pillar-card glass-card">
                      <div className="form-row" style={{ display: 'flex', gap: 12 }}>
                        <div className="form-group" style={{ marginBottom: 0, flex: 1 }}>
                          <input
                            type="text"
                            className="form-input"
                            placeholder="Nombre del pilar"
                            value={pillar.name}
                            onChange={e => handlePillarChange(i, 'name', e.target.value)}
                          />
                        </div>
                        <div className="form-group" style={{ marginBottom: 0, width: 100 }}>
                          <div className="strategy-pillar-pct">
                            <input
                              type="number"
                              className="form-input"
                              placeholder="%"
                              min={0}
                              max={100}
                              value={pillar.percentage || ''}
                              onChange={e =>
                                handlePillarChange(i, 'percentage', Number(e.target.value) || 0)
                              }
                            />
                            <span>%</span>
                          </div>
                        </div>
                      </div>

                      {/* Visual progress bar */}
                      <div className="strategy-pillar-bar-container" style={{ height: 4, background: 'var(--veil-08)', borderRadius: 2, marginTop: 8, overflow: 'hidden' }}>
                        <div 
                          className="strategy-pillar-bar-fill"
                          style={{ 
                            height: '100%',
                            width: `${Math.min(100, Math.max(0, pillar.percentage || 0))}%`,
                            background: pillarError ? 'var(--danger)' : 'var(--primary)',
                            transition: 'width 0.3s ease'
                          }} 
                        />
                      </div>

                      <textarea
                        className="form-textarea strategy-textarea-sm"
                        placeholder="Descripción del pilar..."
                        value={pillar.description}
                        onChange={e => handlePillarChange(i, 'description', e.target.value)}
                        rows={2}
                        style={{ marginTop: 8, width: '100%', resize: 'vertical' }}
                      />
                      <button
                        type="button"
                        className="btn btn-icon btn-sm strategy-pillar-remove"
                        onClick={() => handleRemovePillar(i)}
                        title="Eliminar pilar"
                      >
                        <IconTrash size={14} />
                      </button>
                    </div>
                  ))}
                </div>

                {pillarError && (
                  <p className="strategy-pillar-warning">
                    <IconAlertCircle size={14} />
                    Los porcentajes deben sumar 100% (actual: {pillarTotal}%)
                  </p>
                )}
              </div>
            </div>

            {/* Hashtag Groups */}
            <div className="glass-card strategy-card" style={{ marginTop: 24 }}>
              <div className="strategy-card__header">
                <h3><IconHash size={16} /> Grupos de Hashtags</h3>
              </div>
              <div className="strategy-card__body">
                <HashtagManager groups={hashtagGroups} onChange={setHashtagGroups} />
              </div>
            </div>


          </div>
        </div>
      </div>

      {/* Action bar */}
      <div className="strategy-panel__footer">
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleSave}
          disabled={isSaving}
        >
          {isSaving ? (
            <>Guardando...</>
          ) : (
            <>
              <IconCheck size={16} /> Guardar Estrategia
            </>
          )}
        </button>
      </div>
    </div>
  )
}
